// CoreUI Pro source reference: /Users/guochunzhong/git/oss/coreui-pro/js/src/tooltip.js
import BaseComponent from "./base_component"
import { createPopper } from "@popperjs/core"

function safeHtml(value) {
  const template = document.createElement("template")
  template.innerHTML = String(value)
  template.content.querySelectorAll("script, style, iframe, object").forEach((element) => element.remove())
  template.content.querySelectorAll("*").forEach((element) => {
    if (
      ![
        "A",
        "B",
        "BR",
        "DIV",
        "EM",
        "I",
        "IMG",
        "LI",
        "OL",
        "P",
        "S",
        "SMALL",
        "SPAN",
        "STRONG",
        "SUB",
        "SUP",
        "U",
        "UL",
      ].includes(element.tagName)
    ) {
      element.remove()
      return
    }
    [...element.attributes].forEach((attribute) => {
      const uri = ["href", "src"].includes(attribute.name)
      // Strip control characters before checking URL schemes.
      // eslint-disable-next-line no-control-regex
      const value = attribute.value.replace(/[\u0000-\u0020]/g, "")
      const safeUrl = /^(?!(?:javascript|data|vbscript):)(?:[a-z0-9+.-]+:|[^&:/?#]*(?:[/?#]|$))/i.test(value)
      const safeImage = /^data:image\/(?:bmp|gif|jpeg|jpg|png|tiff|webp);base64,[\d+/a-z=]+$/i.test(value)
      if (
        !["class", "title", "role", "aria-label", "aria-hidden", "alt", "width", "height", "href", "src"].includes(
          attribute.name,
        ) ||
        (uri && !safeUrl && !safeImage)
      )
        element.removeAttribute(attribute.name)
    })
  })
  return template.innerHTML
}

export default class Overlay extends BaseComponent {
  static defaults = { container: "body", placement: "top", trigger: "hover focus", html: false }

  constructor(element, config = {}) {
    super(element, config)
    this.tip = null
    this.popper = null
    this.activeTriggers = new Set()
    this.captureTitle()
    this.bindTriggers()
  }

  captureTitle() {
    const title = this.element.getAttribute("title")
    if (title !== null) {
      this.element.dataset.coreuiOriginalTitle = title
      this.element.removeAttribute("title")
    }
  }

  bindTriggers() {
    String(this.config.trigger)
      .split(/\s+/)
      .forEach((trigger) => {
        if (trigger === "click") this.listen(this.element, "click", () => this.toggle())
        if (trigger === "hover") {
          this.listen(this.element, "mouseenter", () => this.enter("hover"))
          this.listen(this.element, "mouseleave", () => this.leave("hover"))
        }
        if (trigger === "focus") {
          this.listen(this.element, "focusin", () => this.enter("focus"))
          this.listen(this.element, "focusout", () => this.leave("focus"))
        }
      })
  }

  enter(trigger) {
    this.activeTriggers.add(trigger)
    this.show()
  }

  leave(trigger) {
    this.activeTriggers.delete(trigger)
    if (this.activeTriggers.size === 0) this.hide()
  }

  show() {
    if (this.tip || this.transitioning || this.element.disabled) return
    if (this.emit("show", {}, { cancelable: true }).defaultPrevented) return
    const tip = this.createTip()
    if (!tip) return

    this.tip = tip
    const container =
      this.config.container === false
        ? this.element.parentElement
        : document.querySelector(this.config.container) || document.body
    container.append(tip)
    this.generated.push(tip)
    this.element.setAttribute("aria-describedby", tip.id)
    this.popper = createPopper(this.element, tip, { placement: this.config.placement })
    this.transitioning = true
    tip.offsetHeight
    tip.classList.add("show")
    this.afterTransition(tip, () => {
      this.transitioning = false
      this.emit("shown")
      if (this.pendingHide) {
        this.pendingHide = false
        this.hide()
      }
    })
  }

  hide() {
    if (this.tip && this.transitioning) {
      this.pendingHide = true
      return
    }
    if (!this.tip || this.transitioning || this.emit("hide", {}, { cancelable: true }).defaultPrevented) return
    const tip = this.tip
    this.transitioning = true
    tip.classList.remove("show")
    this.afterTransition(tip, () => {
      this.removeTip()
      this.transitioning = false
      this.emit("hidden")
    })
  }

  removeTip() {
    this.generated = this.generated.filter((element) => element !== this.tip)
    this.tip?.remove()
    this.popper?.destroy()
    this.popper = null
    this.tip = null
    this.restoreAttribute("aria-describedby")
  }

  toggle() {
    this.tip ? this.hide() : this.show()
  }

  setContent(element, value) {
    if (this.config.html) element.innerHTML = safeHtml(value)
    else element.textContent = value
  }

  dispose() {
    if (this.signal.aborted) return
    this.removeTip()
    this.restoreAttribute("title")
    this.restoreAttribute("data-coreui-original-title")
    super.dispose()
  }
}
